import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { oauthApplications } from '@shopmy/universal';
import { fetchOAuthApplications, createOAuthApplication, updateOAuthApplication } from '../../api/oauth_applications';
import './OAuth.scss';

const { OAUTH_APPLICATION_TYPE_LABELS, OAUTH_APPLICATION_TYPES } = oauthApplications;

const EMPTY_FORM = {
  appName: '',
  user: '',
  redirectUrls: '',
  allowAutomaticRegistration: false,
  type: 'STANDARD',
  createDailyRequestLimit: '',
  readDailyRequestLimit: ''
};

const limitToInput = value => (value == null ? '' : String(value));
const inputToLimit = value => (value === '' ? null : Number(value));
const formToAgentAppRateLimits = form => ({
  createDailyRequestLimit: inputToLimit(form.createDailyRequestLimit),
  readDailyRequestLimit: inputToLimit(form.readDailyRequestLimit)
});
const formatLimit = (value, fallback) => (value == null ? `${fallback.toLocaleString()} (default)` : value.toLocaleString());

const OAuth = () => {
  const [applications, setApplications] = useState([]);
  const [rateLimitDefaults, setRateLimitDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create / edit form; `editing` holds the row being edited, null when creating
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOAuthApplications();
      setApplications(data?.applications || []);
      setRateLimitDefaults(data?.agentAppRateLimitDefaults || {});
    } catch {
      setError('Failed to load OAuth applications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
  }, []);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const startCreating = () => {
    setCreated(null);
    if (showForm && !editing) return closeForm();
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEditing = application => {
    setCreated(null);
    setEditing(application);
    setForm({
      appName: application.appName || '',
      user: application.developer_username ? `@${application.developer_username}` : '',
      redirectUrls: application.redirectUrls || '',
      allowAutomaticRegistration: !!application.allowAutomaticRegistration,
      type: application.type,
      createDailyRequestLimit: limitToInput(application.agentAppRateLimits?.createDailyRequestLimit),
      readDailyRequestLimit: limitToInput(application.agentAppRateLimits?.readDailyRequestLimit)
    });
    setShowForm(true);
  };

  const handleUpdate = async () => {
    const { application } = await updateOAuthApplication({
      id: editing.id,
      appName: form.appName.trim(),
      redirectUrls: form.redirectUrls,
      allowAutomaticRegistration: form.allowAutomaticRegistration,
      agentAppRateLimits: formToAgentAppRateLimits(form)
    });
    setApplications(prev => prev.map(row => (row.id === application.id ? { ...row, ...application } : row)));
    closeForm();
    window.ALERT?.success?.('OAuth application updated.');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setCreated(null);
    try {
      if (editing) return await handleUpdate();
      const result = await createOAuthApplication({
        appName: form.appName.trim(),
        user: form.user.trim(),
        redirectUrls: form.redirectUrls,
        allowAutomaticRegistration: form.allowAutomaticRegistration,
        type: form.type,
        agentAppRateLimits: formToAgentAppRateLimits(form)
      });

      const { application, developer } = result;
      const newRow = {
        id: application.id,
        appName: application.appName,
        redirectUrls: application.redirectUrls,
        allowAutomaticRegistration: application.allowAutomaticRegistration,
        type: application.type,
        agentAppRateLimits: application.agentAppRateLimits,
        createdAt: new Date().toISOString(),
        developer_id: developer.id,
        developer_name: developer.name,
        developer_username: developer.username,
        developer_email: developer.email
      };

      setApplications(prev => [newRow, ...prev]);
      setCreated(result);
      closeForm();
      window.ALERT?.success?.('OAuth application created.');
    } catch (err) {
      const msg = err?.response?.data?.error || (editing ? 'Failed to update OAuth application.' : 'Failed to create OAuth application.');
      window.ALERT?.error?.(msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = async key => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable; the key is still visible to copy manually.
    }
  };

  const sampleAuthorizeUrl = created
    ? `https://shopmy.us/oauth?Developer_id=${created.developer.id}&scopes=read_profile,read_links,write_links&redirect_url=${encodeURIComponent(
        (created.application.redirectUrls || '').split(',')[0] || ''
      )}`
    : '';

  return (
    <div className='oauth-admin-outer'>
      <div className='oauth-admin-header'>
        <div>
          <h1 className='oauth-admin-title'>OAuth Applications</h1>
          <div className='oauth-admin-subtitle'>External partner developer apps for the Partner API.</div>
        </div>
        <button className='oauth-admin-new-btn' onClick={startCreating}>
          {showForm && !editing ? 'Cancel' : '+ New Application'}
        </button>
      </div>

      {/* Created result — developer key is shown only once */}
      {created && (
        <div className='oauth-admin-created'>
          <div className='oauth-admin-created-title'>Application "{created.application.appName}" created</div>
          <div className='oauth-admin-created-note'>Copy the developer key now — it is only shown here, once.</div>
          <div className='oauth-admin-created-grid'>
            <div className='oauth-admin-created-field'>
              <span className='label'>Developer_id</span>
              <code>{created.developer.id}</code>
            </div>
            <div className='oauth-admin-created-field'>
              <span className='label'>Developer Key</span>
              <div className='oauth-admin-key-row'>
                <code>{created.developerKey}</code>
                <button type='button' onClick={() => copyKey(created.developerKey)}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <div className='oauth-admin-created-field'>
              <span className='label'>Registration Code</span>
              <code>{created.registrationCode}</code>
            </div>
            <div className='oauth-admin-created-field full'>
              <span className='label'>Sample authorize URL</span>
              <code className='wrap'>{sampleAuthorizeUrl}</code>
            </div>
          </div>
        </div>
      )}

      {/* Create / edit form. Type and developer are fixed once an application exists. */}
      {showForm && (
        <form className='oauth-admin-form' onSubmit={handleSubmit}>
          {editing && <div className='oauth-admin-form-title'>Editing application {editing.id}</div>}
          <div className='oauth-admin-form-row'>
            <label>App name</label>
            <input type='text' value={form.appName} onChange={e => updateField('appName', e.target.value)} placeholder='Acme Creator Tools' />
          </div>
          <div className='oauth-admin-form-row'>
            <label>Type</label>
            <select value={form.type} onChange={e => updateField('type', e.target.value)} disabled={!!editing}>
              {Object.entries(OAUTH_APPLICATION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <div className='oauth-admin-form-hint'>
              Shopping Agent apps mint links through the API with their developer key and skip the consent flow.
            </div>
          </div>
          <div className='oauth-admin-form-row'>
            <label>Developer user</label>
            <input
              type='text'
              value={form.user}
              onChange={e => updateField('user', e.target.value)}
              placeholder='User id, email, or username'
              disabled={!!editing}
            />
            <div className='oauth-admin-form-hint'>Must be an existing user. They become the app owner (Developer_id + developer key).</div>
          </div>
          <div className='oauth-admin-form-row'>
            <label>Redirect URLs</label>
            <input
              type='text'
              value={form.redirectUrls}
              onChange={e => updateField('redirectUrls', e.target.value)}
              placeholder='https://app.acme.com/oauth-callback, https://...'
            />
            <div className='oauth-admin-form-hint'>
              Comma-separated allowlist. Exact match, including scheme.{' '}
              {form.type === OAUTH_APPLICATION_TYPES.SHOPPING_AGENT
                ? 'Optional for shopping agents, which may or may not use the consent flow.'
                : 'Required.'}
            </div>
          </div>
          <div className='oauth-admin-form-row inline'>
            <label className='checkbox'>
              <input
                type='checkbox'
                checked={form.allowAutomaticRegistration}
                onChange={e => updateField('allowAutomaticRegistration', e.target.checked)}
              />
              Allow automatic registration
            </label>
          </div>
          {form.type === OAUTH_APPLICATION_TYPES.SHOPPING_AGENT && (
            <div className='oauth-admin-form-row'>
              <label>Agent link rate limits (requests per UTC day)</label>
              <div className='oauth-admin-form-pair'>
                <input
                  type='number'
                  min='0'
                  step='1'
                  value={form.createDailyRequestLimit}
                  onChange={e => updateField('createDailyRequestLimit', e.target.value)}
                  placeholder={`Create: ${limitToInput(rateLimitDefaults.createDailyRequestLimit)}`}
                />
                <input
                  type='number'
                  min='0'
                  step='1'
                  value={form.readDailyRequestLimit}
                  onChange={e => updateField('readDailyRequestLimit', e.target.value)}
                  placeholder={`Read: ${limitToInput(rateLimitDefaults.readDailyRequestLimit)}`}
                />
              </div>
              <div className='oauth-admin-form-hint'>Blank keeps the default. 0 refuses every request to that endpoint.</div>
            </div>
          )}
          <div className='oauth-admin-form-actions'>
            {editing && (
              <button type='button' className='secondary' onClick={closeForm}>
                Cancel
              </button>
            )}
            <button type='submit' disabled={submitting}>
              {submitting ? 'Saving…' : editing ? 'Save changes' : 'Create application'}
            </button>
          </div>
        </form>
      )}

      {/* Applications table */}
      {loading ? (
        <div className='oauth-admin-state'>Loading…</div>
      ) : error && !applications.length ? (
        <div className='oauth-admin-state error'>{error}</div>
      ) : !applications.length ? (
        <div className='oauth-admin-state'>No OAuth applications yet.</div>
      ) : (
        <table className='oauth-admin-table'>
          <thead>
            <tr>
              <th>ID</th>
              <th>App</th>
              <th>Type</th>
              <th>Developer</th>
              <th>Redirect URLs</th>
              <th>Auto-Reg</th>
              <th>Agent limits / day</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.id}>
                <td>{app.id}</td>
                <td>{app.appName}</td>
                <td>{OAUTH_APPLICATION_TYPE_LABELS[app.type] || app.type}</td>
                <td>
                  {app.developer_id ? (
                    <div className='oauth-admin-dev'>
                      <span>{app.developer_name || '—'}</span>
                      <span className='muted'>
                        @{app.developer_username} · {app.developer_id}
                      </span>
                    </div>
                  ) : (
                    <span className='muted'>—</span>
                  )}
                </td>
                <td className='oauth-admin-urls'>
                  {(app.redirectUrls || '')
                    .split(',')
                    .filter(Boolean)
                    .map(url => (
                      <div key={url} className='oauth-admin-url'>
                        {url}
                      </div>
                    ))}
                </td>
                <td>{app.allowAutomaticRegistration ? 'Yes' : 'No'}</td>
                <td>
                  {app.type === OAUTH_APPLICATION_TYPES.SHOPPING_AGENT ? (
                    <div className='oauth-admin-limits'>
                      <span>
                        Create {formatLimit(app.agentAppRateLimits?.createDailyRequestLimit, rateLimitDefaults.createDailyRequestLimit ?? 0)}
                      </span>
                      <span>Read {formatLimit(app.agentAppRateLimits?.readDailyRequestLimit, rateLimitDefaults.readDailyRequestLimit ?? 0)}</span>
                    </div>
                  ) : (
                    <span className='muted'>—</span>
                  )}
                </td>
                <td>{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '—'}</td>
                <td>
                  <button type='button' className='oauth-admin-icon-btn' title='Edit application' onClick={() => startEditing(app)}>
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OAuth;
